import cheerio from "cheerio";
import { reportError } from "~/lib/logging/logHelper";
import {
  DictionaryItem,
  filterDictionaryItemsWithRegexp,
} from "~/models/dictionaryItem";

type RegexReplaceData = { regexp: string; url: string };
export const REPLACE_TOPIC_URL_VARIABLE_NAME = "VAR_REPLACE_TOPIC_URL";
// skip for content in link and twitter card
const NON_FORMATTED_TAG_NAMES = [
  ":not(div.md__LinkArticle *, div.md__twitter *) > p",
  ":not(div.md__LinkArticle *, div.md__twitter *) > span",
  ":not(div.md__LinkArticle *, div.md__twitter *) > div",
];

export function generateSubTopicRegexReplaceList(
  dictionaryItems: DictionaryItem[]
): RegexReplaceData[] | undefined {
  return (
    filterDictionaryItemsWithRegexp(dictionaryItems)
      // priority the list by name length
      ?.sort((a, b) => b.name.length - a.name.length)
      ?.map((dictionary) => ({
        regexp: dictionary.regexp,
        // url may contain difference host and locale, it will be replaced when rendering in full view QA
        url: `${REPLACE_TOPIC_URL_VARIABLE_NAME}/s/${dictionary.slug}`,
      }))
  );
}

export function replaceSubTopicUrl({
  html,
  regexReplaceList,
}: {
  html: string;
  regexReplaceList?: RegexReplaceData[];
}): string {
  if (!regexReplaceList) {
    return html;
  }

  try {
    const $ = cheerio.load(html, {
      decodeEntities: false,
      xmlMode: false,
    });

    NON_FORMATTED_TAG_NAMES.map((tagName) => {
      $(tagName)
        .contents()
        .each(function (i, ele) {
          // only process for text node
          if (ele.type !== "text") {
            return;
          }

          const classNames = $(ele).attr("class");
          // skip for formatted tag
          if (classNames) {
            return;
          }

          const text = $(ele).text();
          const replacedText = replaceTextByRegex({
            regexReplaceList,
            text,
          });
          // replace old content only when the new content is different
          if (replacedText !== text) {
            $(ele).replaceWith($(replacedText));
          }
        });
    });

    return $("body")?.html() || html;
  } catch (e) {
    reportError({
      errorInfo: { message: e?.message, stack: e?.stack },
      errorMessage: "Error occur when replace subtopic url",
    });
    return html;
  }
}

const replaceTextByRegex = ({
  regexReplaceList,
  text,
}: {
  regexReplaceList: RegexReplaceData[];
  text: string;
}): string => {
  // replace all matched subtopic in the paragraph
  let result = text;
  for (const regexData of regexReplaceList) {
    try {
      // prevent overlap subtopic, find the match outside the replaced area
      const reg = new RegExp(
        `(?!<a[^>]*>[^<])${regexData.regexp}(?![^<]*</a>)`,
        "ig"
      );
      const match = reg.exec(result);
      if (!match || !match[0]) {
        continue;
      }

      result = `${result.slice(0, match.index)}<a href=${regexData.url}>${
        match[0]
      }</a>${result.slice(match.index + match[0].length)}`;
    } catch {
      continue;
    }
  }

  return result;
};
